import { generateOperationKey } from "@orpc/tanstack-query";
import { UserAddDialog } from "@/components/admin/users/add-dialog";
import { UserTable } from "@/components/admin/users/table";
import { ADMIN_USER_ROLES_FILTER } from "@papier/auth-contracts";
import { Input } from "@papier/ui/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@papier/ui/components/ui/select";
import { createFileRoute } from "@tanstack/react-router";
import {
  Magnifier as Search,
  Shield,
  User,
  UsersGroupRounded as Users,
} from "@papier/ui/components/icons";
import { useDeferredValue, useMemo } from "react";
import { z } from "zod";

const usersSearchSchema = z.object({
  email: z
    .string()
    .optional()
    .transform((val) => (val?.length === 0 ? undefined : val)),
  role: z.enum(ADMIN_USER_ROLES_FILTER).catch("all"),
  page: z.number().int().min(1).catch(1),
  sortBy: z.string().optional(),
  sortDir: z.enum(["asc", "desc"]).optional(),
});

export const Route = createFileRoute("/admin/users")({
  component: UsersPage,
  validateSearch: usersSearchSchema,
});

function UsersPage() {
  const { useNavigate, useSearch, useRouteContext } = Route;
  const navigate = useNavigate();
  const { queryClient } = useRouteContext();
  const { email, role, page, sortBy, sortDir } = useSearch();

  const limit = 10;
  const deferredEmail = useDeferredValue(email);

  const queryInput = useMemo(
    () => ({
      email: deferredEmail,
      role: role !== "all" ? role : undefined,
      sortBy,
      sortDirection: sortDir,
      page,
      limit,
    }),
    [deferredEmail, role, page, limit, sortBy, sortDir],
  );

  return (
    <div className="space-y-4">
      {/* Filters */}
      <nav className="mb-2 flex w-full flex-wrap items-end justify-between gap-2">
        <div className="flex items-end gap-2">
          <div className="relative">
            <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search..."
              className="pl-10"
              value={email || ""}
              onChange={(e) =>
                navigate({
                  search: (prev) => ({ ...prev, email: e.target.value, page: 1 }),
                  replace: true,
                  resetScroll: false,
                })
              }
            />
          </div>
          <Select
            value={role}
            onValueChange={(value) =>
              navigate({
                search: (prev) => ({
                  ...prev,
                  role: value as (typeof ADMIN_USER_ROLES_FILTER)[number],
                  page: 1,
                }),
                replace: true,
                resetScroll: false,
              })
            }
          >
            <SelectTrigger className="flex w-[150px] items-center gap-2">
              <span className="flex items-center gap-2">
                {role === "all" ? <Users /> : role === "admin" ? <Shield /> : <User />}
                {role === "all" ? "All Roles" : role.charAt(0).toUpperCase() + role.slice(1)}
              </span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                <span className="flex items-center gap-2">
                  <Users />
                  All Roles
                </span>
              </SelectItem>
              <SelectItem value="admin">
                <span className="flex items-center gap-2">
                  <Shield />
                  Admin
                </span>
              </SelectItem>
              <SelectItem value="user">
                <span className="flex items-center gap-2">
                  <User />
                  User
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <UserAddDialog
          onSuccess={() =>
            queryClient.invalidateQueries({
              queryKey: generateOperationKey(["authTrpc", "getAdminUsers"]),
            })
          }
        />
      </nav>

      <UserTable
        onSuccess={() =>
          queryClient.invalidateQueries({
            queryKey: generateOperationKey(["authTrpc", "getAdminUsers"]),
          })
        }
        filters={queryInput}
      />
    </div>
  );
}
